// Horsir i18n — Chinese (zh) / English (en) strings.
import {
  BRAND_NAME,
  BRAND_SLUG,
  brandAgentName,
  brandGreeting,
  brandAssistantLabel,
} from "./brand";

export type Lang = "zh" | "en";

// ---- boot ----
const BOOT = {
  booting:          { zh: "正在连接 Office…",               en: "Connecting to Office…" },
  pageLoading:      { zh: "页面加载中…",                     en: "Loading page…" },
  loadError:        { zh: "加载出错：",                       en: "Load error: " },
  initRejected:     { zh: "初始化被拒绝：",                  en: "Initialization rejected: " },
  officeTimeout:    { zh: "Office 长时间未就绪。请依次尝试：① 关闭并重新打开本侧栏；② 完全退出该 Office 程序再重开；③ 清除 Office 加载项缓存（见说明）；④ 确认已 npm run dev-certs 且证书受信任。",
                         en: "Office not ready. Try: ① Close & reopen this add-in pane; ② Quit & restart the Office app; ③ Clear the Office add-in cache; ④ Ensure npm run dev-certs has been run and the cert is trusted." },
  initProblem:      { zh: "初始化时出现问题：",              en: "Problem during init: " },
  missingDom:       (id: string) => ({ zh: `缺少 DOM 元素 #${id}（HTML 与 TS 不同步？）`, en: `Missing DOM element #${id} (HTML/TS out of sync?)` }),

  // context strip
  currentCtx:       { zh: "当前上下文",                       en: "Context" },
  refresh:          { zh: "刷新",                             en: "Refresh" },
  reading:          { zh: "读取中…",                          en: "Reading…" },
  selChars:         (n: number) => ({ zh: `已选中 ${n} 字`,   en: `${n} chars selected` }),
  selEmptyWord:     { zh: "未选中（操作将作用于光标或全文）", en: "No selection (operation will act on cursor or full body)" },
  sheetCtx:         (sheet: string, addr: string) => ({ zh: `工作表「${sheet}」 · 选区 ${addr || "—"}`, en: `Sheet "${sheet}" · Range ${addr || "—"}` }),
  selEmptyPpt:      { zh: "未选中文本框",                    en: "No text box selected" },
  unsupportedHost:  { zh: "不支持的宿主",                    en: "Unsupported host" },
  ctxReadFailed:     { zh: "读取失败：",                      en: "Read failed: " },
};

// ---- status ----
const STATUS = {
  notConfigured:    { zh: "请先配置接口地址与模型",           en: "Please configure Base URL & model first" },
  noBackend:        { zh: "未检测到服务端 Agent，无法联网搜索（请先 npm run server）", en: "Server Agent not detected. Run npm run server for web search." },
  searchTesting:    { zh: "搜索测试中…",                     en: "Testing search…" },
  searchOk:         (n: number) => ({ zh: `搜索可用：返回 ${n} 条结果`, en: `Search works: ${n} results` }),
  searchFailed:      { zh: "搜索失败：",                      en: "Search failed: " },
  agentConnected:   { zh: "服务端 Agent 已连接，可联网搜索。", en: "Server Agent connected. Web search available." },
  agentDisconnected:{ zh: "未检测到服务端 Agent（运行 npm run server 后可联网搜索）。", en: "No server Agent. Run npm run server to enable web search." },
  netSearchTitle:   { zh: "联网搜索（经服务端 Agent）",        en: "Web search (via server Agent)" },
  netSearchNoAgent: { zh: "需先启动服务端 Agent",             en: "Server Agent required" },
  testConnecting:   { zh: "测试中…",                          en: "Connecting…" },
  testOk:           { zh: "连接成功：",                        en: "Connected: " },
  testFailed:        { zh: "失败：",                           en: "Failed: " },
  saved:            { zh: "已保存",                            en: "Saved" },
  fillUrlModel:     { zh: "请填写接口地址与模型",              en: "Please fill in Base URL & model" },

  netSearching:     { zh: "联网检索中…",                      en: "Searching web…" },
  thinking:         { zh: "思考中…",                          en: "Thinking…" },
  cleaning:         { zh: "整理内容中…",                      en: "Cleaning content…" },
  executing:        (names: string) => ({ zh: `正在执行：${names}…`, en: `Executing: ${names}…` }),
  done:             { zh: "完成",                              en: "Done" },
  cancelled:        { zh: "已取消。",                          en: "Cancelled." },
  userCancelled:    { zh: "用户已取消",                       en: "Cancelled by user" },
  errorPrefix:      { zh: "出错：",                            en: "Error: " },
  enterMsg:         { zh: "请输入消息",                        en: "Type a message" },
};

// ---- apply / output actions ----
const ACTIONS = {
  replaceSel:       { zh: "替换选区",                         en: "Replace" },
  insertAfter:      { zh: "后面插入",                         en: "Insert after" },
  appendEnd:        { zh: "追加文末",                         en: "Append" },
  addComment:       { zh: "加为批注",                         en: "Comment" },
  writeAnalysis:    { zh: "写入分析表",                       en: "Write to sheet" },
  replaceSelPpt:    { zh: "替换选中",                         en: "Replace" },
  insertSlide:      { zh: "插入为新页",                       en: "Insert slide" },
  writing:          { zh: "写入中…",                          en: "Writing…" },
  applied:          { zh: "已应用到文档",                     en: "Applied to document" },
  copied:           { zh: "已复制",                           en: "Copied" },
  copyFailed:       { zh: "复制失败",                         en: "Copy failed" },
  wroteSheet:       (name: string) => ({ zh: `已写入「${name}」`, en: `Written to "${name}"` }),
  slideInserted:    (n: number, title: string) => ({ zh: `已插入第 ${n} 页：${title}`, en: `Slide ${n} inserted: ${title}` }),
  newSlideDefault:  { zh: "新幻灯片",                         en: "New Slide" },
  unsupportedHostPanel: { zh: "当前宿主不受支持。",           en: "Current host not supported." },
};

// ---- export ----
const EXPORT = {
  chatTitle:        { zh: `# ${BRAND_NAME} 对话记录`,                 en: `# ${BRAND_NAME} Chat Log` },
  metaLine:         (model: string, date: string) => ({ zh: `> 模型：${model} · 导出时间：${date}`, en: `> Model: ${model} · Export time: ${date}` }),
  userLabel:        { zh: "## 我",                            en: "## Me" },
  assistantLabel:   { zh: brandAssistantLabel(),              en: brandAssistantLabel() },
  fileName:         (stamp: string) => ({ zh: `${BRAND_SLUG}-对话-${stamp}.md`, en: `${BRAND_SLUG}-chat-${stamp}.md` }),
  allHistoryTitle:  { zh: `# ${BRAND_NAME} 对话历史（全部导出）`,        en: `# ${BRAND_NAME} Chat History (All)` },
  allHistoryFileName:(stamp: string) => ({ zh: `${BRAND_SLUG}-历史-${stamp}.md`, en: `${BRAND_SLUG}-history-${stamp}.md` }),
  exported:         { zh: "已导出 Markdown 文件",              en: "Markdown file exported" },
  copiedToClip:     { zh: "已复制对话到剪贴板",               en: "Chat copied to clipboard" },
  exportFailed:     { zh: "导出失败",                         en: "Export failed" },
  noChatToExport:   { zh: "还没有对话可导出",                 en: "No chat to export yet" },
};

// ---- model footer ----
const FOOTER = {
  noModel:          { zh: "未设置模型",                       en: "No model set" },
  agentOk:          { zh: "服务端 Agent 已连接",              en: "Agent connected" },
  direct:           { zh: "直连模式",                         en: "Direct mode" },
};

// ---- welcome cards ----
const WELCOME = {
  word: {
    emoji: "📝",
    greet:   { zh: brandGreeting("zh"), en: brandGreeting("en") },
    sub:     { zh: "Word 文档助手。可读取/搜索全文，改写、润色、总结、翻译，也能批量高亮关键词、加批注、查找替换，或直接插入标题、段落、表格。",
               en: "Word document assistant. Read/search the document, rewrite, polish, summarize, translate, highlight keywords in batch, add comments, find-and-replace, and insert headings, paragraphs, or tables." },
    caps: {
      zh: ["读取选区/全文、搜索关键词、列出已高亮内容", "改写、润色、扩写、续写、翻译", "批量高亮关键词、按搜索文本加批注、查找替换", "插入标题/段落/表格并设置格式、查看大纲"],
      en: ["Read selection/document, search text, list existing highlights", "Rewrite, polish, expand, continue, translate", "Batch-highlight keywords, add comments by search, find & replace", "Insert headings/paragraphs/tables, view outline"],
    },
    groups: [
      { label: { zh: "全文标注与检索", en: "Search & annotate" },
        questions: {
          zh: ["把全文涉及 AI 智能化的关键词用蓝色高亮出来", "在所有提到'预算'的段落加批注：核实数据来源", "全文哪些段落提到了合规要求？列出来"],
          en: ["Highlight every AI-related term in blue across the document", "Add a comment on every paragraph mentioning 'budget': verify source", "List every paragraph that mentions compliance"] }},
      { label: { zh: "处理选中内容", en: "Process selection" },
        questions: {
          zh: ["请改写我选中的这段文字，使其更清晰", "请润色这段内容，优化用词和语气", "请总结这段文字的要点"],
          en: ["Rewrite the selected text to be clearer", "Polish this content for better wording and tone", "Summarize the key points of this text"] }},
      { label: { zh: "生成与编辑", en: "Generate & edit" },
        questions: {
          zh: ["帮我在文档开头加一个居中的标题", "把全文的'公司'全部替换成'集团'", "请把这段整理成三栏表格"],
          en: ["Add a centered title at the start of the document", "Replace every 'Company' with 'Group' across the document", "Format this into a three-column table"] }},
    ],
  },
  excel: {
    emoji: "📊",
    greet:   { zh: brandGreeting("zh"), en: brandGreeting("en") },
    sub:     { zh: "Excel 数据助手。可解读选区或当前工作表，定位单元格，分组聚合、统计列画像、找重复值，写公式、生成图表/透视表，也能把结果写入新的工作表。",
               en: "Excel data assistant. Analyze selection/sheet, locate cells, group-by, profile columns, find duplicates, write formulas, generate charts/pivots, and write results to new sheets." },
    caps: {
      zh: ["读取选区/工作表，按值定位单元格", "分组聚合、列统计画像、找重复值与缺失值", "写出对应的 Excel 公式并解释", "生成「AI 分析/图表/透视表」工作表"],
      en: ["Read selection/sheet, find cells by value", "Group-by aggregation, column profile, duplicates & missing values", "Write Excel formulas with explanations", "Generate analysis/chart/pivot sheets"],
    },
    groups: [
      { label: { zh: "定位与梳理", en: "Locate & profile" },
        questions: {
          zh: ["哪些单元格的值小于 60？标出来", "请列出每列的均值、最大值、空值数", "找出表格中所有重复的记录"],
          en: ["Which cells have values below 60?", "Profile each column: mean, max, missing count", "Find all duplicate rows in the sheet"] }},
      { label: { zh: "分组与聚合", en: "Group & aggregate" },
        questions: {
          zh: ["按地区对销售额求和，生成汇总", "按月份分组，看每月的平均订单量", "按部门和产品类别做交叉统计"],
          en: ["Sum sales by region", "Group by month and show average order volume", "Cross-tabulate by department and product category"] }},
      { label: { zh: "理解与生成", en: "Understand & generate" },
        questions: {
          zh: ["请解读当前选区的数据，指出值得注意的点", "我想计算每行的合计，请给出公式", "请总结这份表的总体情况"],
          en: ["Analyze the selected data and highlight notable points", "Give me a formula to sum each row", "Summarize the overall situation of this sheet"] }},
    ],
  },
  powerpoint: {
    emoji: "📽️",
    greet:   { zh: brandGreeting("zh"), en: brandGreeting("en") },
    sub:     { zh: "PowerPoint 演示助手。可改写选中文本框、读取/精简当前页、通读全篇，也能插入新幻灯片（标题+要点）。",
               en: "PowerPoint presentation assistant. Rewrite selected text boxes, read/trim the current slide, read all slides, and insert new slides (title + bullets)." },
    caps: {
      zh: ["读取选中文本框、所有幻灯片、以及当前正在查看的页", "改写、润色、扩写、精简选中文字", "生成单页/多页幻灯片（标题+要点）", "通读全篇并总结演示主线"],
      en: ["Read selected text box, all slides, or the slide you're currently viewing", "Rewrite, polish, expand, trim selected text", "Generate single/multiple slides (title + bullets)", "Read all slides and summarize the narrative"],
    },
    groups: [
      { label: { zh: "基于当前页", en: "On current slide" },
        questions: {
          zh: ["我现在停在第几页？这一页讲的是什么？", "我当前这一页要点太多，帮我精简成 3 条", "围绕本页主题，再加一页延续"],
          en: ["Which slide am I on, and what's it about?", "My current slide has too many bullets — trim to 3", "Add a follow-up slide on the same topic"] }},
      { label: { zh: "改写文案", en: "Rewrite text" },
        questions: {
          zh: ["请改写选中的文字，使其更适合演示", "请把这句要点扩成完整表述", "请把选中文字翻译成英文"],
          en: ["Rewrite the selected text for presentation", "Expand this bullet into a full sentence", "Translate the selected text into English"] }},
      { label: { zh: "生成与通读", en: "Generate & overview" },
        questions: {
          zh: ["请围绕这个主题生成一页幻灯片：3-5 个要点", "请通读全部幻灯片，总结这份演示的主线", "请把下面这段内容拆成两页幻灯片"],
          en: ["Generate a slide on this topic with 3-5 bullet points", "Read all slides and summarize the main thread", "Split this content into two slides"] }},
    ],
  },
};
const WELCOME_UNSUPPORTED = {
  zh: "当前宿主暂不支持，请在 Word / Excel / PowerPoint 中打开。",
  en: "Host not supported. Please open in Word, Excel, or PowerPoint.",
};

// ---- settings labels ----
const SETTINGS: Record<string, { zh: string; en: string }> = {
  presetCustom:     { zh: "自定义",                           en: "Custom" },
  
  // Theme names
  themeDefault:     { zh: "默认",                              en: "Default" },
  themeBlue:        { zh: "科技蓝",                            en: "Tech Blue" },
  themeOrange:      { zh: "暖橙",                              en: "Warm Orange" },
  themeRed:         { zh: "政务红",                            en: "Government Red" },

  modelSettings:    { zh: "模型设置",                          en: "Model Settings" },
  theme:            { zh: "主题",                              en: "Theme" },
  language:         { zh: "语言 / Language",                   en: "Language" },
  preset:           { zh: "预设",                              en: "Preset" },
  baseUrl:          { zh: "接口地址 (Base URL)",                en: "Base URL" },
  baseUrlHint:      { zh: '需以 /v1 结尾，将自动调用 <code>/chat/completions</code>。', en: 'Must end with /v1; calls <code>/chat/completions</code>.' },
  apiKey:           { zh: "API Key",                           en: "API Key" },
  model:            { zh: "模型",                              en: "Model" },
  temperature:      { zh: "温度",                              en: "Temperature" },
  testConn:         { zh: "测试连接",                          en: "Test Connection" },
  save:             { zh: "保存",                              en: "Save" },
  keyHint:          { zh: "密钥保存在本机浏览器存储中，请勿在公共设备上保存。", en: "Keys are stored in your local browser storage. Do not save on public devices." },
  webSearch:        { zh: "联网搜索（经服务端 Agent）",         en: "Web Search (via server Agent)" },
  searchProvider:   { zh: "搜索来源",                          en: "Search Source" },
  // Search provider names
  searchGlm:        { zh: "智谱 GLM（需 API Key）",             en: "Zhipu GLM (requires API Key)" },
  searchTavily:     { zh: "Tavily（需 API Key）",               en: "Tavily (requires API Key)" },
  searchSearxng:    { zh: "SearXNG（自建实例）",                en: "SearXNG (self-hosted instance)" },
  searchBocha:      { zh: "博查 Bocha（需 API Key）",            en: "Bocha (requires API Key)" },
  searchBaidu:      { zh: "百度 AI 搜索（需 API Key）",          en: "Baidu AI Search (requires API Key)" },
  // Search provider notes
  searchGlmNote:    { zh: "智谱 web_search，中英文俱佳，需填写 GLM API Key。", en: "Zhipu web_search, supports Chinese and English, requires GLM API Key." },
  searchTavilyNote: { zh: "AI 搜索，有免费额度，需填写 API Key。", en: "AI search with free tier, requires API Key." },
  searchSearxngNote:{ zh: "填写你的 SearXNG 实例地址，完全免费、自托管。", en: "Enter your SearXNG instance URL, completely free and self-hosted." },
  searchBochaNote:  { zh: "国内 AI 搜索 API，中英文俱佳，需填写博查 API Key。", en: "Chinese AI search API, supports Chinese and English, requires Bocha API Key." },
  searchBaiduNote:  { zh: "百度千帆 AI 搜索，国内直连、质量高，需填写 BCE API Key。", en: "Baidu Qianfan AI search, direct in mainland China, requires BCE API Key." },
  searchApiKey:     { zh: "搜索 API Key",                      en: "Search API Key" },
  searchEndpoint:   { zh: "实例地址",                          en: "Instance Address" },
  getKeyLink:       { zh: "没有 Key？点此前往官网获取 →",        en: "No key yet? Get one here →" },
  viewDocsLink:     { zh: "查看部署文档 →",                     en: "View deployment docs →" },
  testSearch:       { zh: "测试搜索",                          en: "Test Search" },
  drawSearch:       { zh: "联网搜索（经服务端 Agent）",         en: "Web Search (via server Agent)" },
  exportConfig:     { zh: "导出配置",                           en: "Export Config" },
  importConfig:     { zh: "导入配置",                           en: "Import Config" },
  configExported:   { zh: "配置已导出",                         en: "Config exported" },
  configImported:   { zh: "配置已导入",                         en: "Config imported" },
  importFailed:     { zh: "导入失败：",                         en: "Import failed: " },

  // context window control
  contextConfig:    { zh: "上下文控制",                         en: "Context Window" },
  contextMaxTokens: { zh: "上下文窗口大小 (K tokens)",       en: "Context Window (K tokens)" },
  contextMaxTokensHint: { zh: "超过此值将触发历史裁剪。预设模型会自动填充推荐值。", en: "History is trimmed when this limit is approached. Presets auto-fill recommended values." },
  contextSummaryThreshold: { zh: "摘要化阈值",                  en: "Summary Threshold" },
  contextSummaryThresholdHint: { zh: "当预估 token 超过窗口的该百分比时，自动将较早的对话轮次压缩为摘要。", en: "When estimated tokens exceed this percentage of the window, older turns are summarised." },

  // topbar / composer / actions
  btnHistory:       { zh: "对话历史",                           en: "Chat History" },
  btnNewChat:       { zh: "新对话",                             en: "New Chat" },
  btnSettings:      { zh: "设置",                               en: "Settings" },
  currentCtx:       { zh: "当前上下文",                          en: "Context" },
  refreshCtx:       { zh: "刷新",                               en: "Refresh" },
  exportChat:       { zh: "导出",                               en: "Export" },
  exportAllHistory: { zh: "导出全部",                           en: "Export All" },
  copyLast:         { zh: "复制",                               en: "Copy" },
  webSearchBtn:     { zh: "🔎 联网",                            en: "🔎 Search" },
  sendBtn:          { zh: "发送",                               en: "Send" },
  composerPlaceholder: { zh: "输入消息，Enter 发送，Shift+Enter 换行…", en: "Type a message, Enter to send, Shift+Enter for newline…" },

  // history
  historyTitle:     { zh: "对话历史",                           en: "Chat History" },
  historyEmpty:     { zh: "暂无历史对话",                       en: "No history yet" },
  historySaved:     { zh: "已保存到历史记录",                    en: "Saved to history" },
  historyRestored:  { zh: "已恢复对话",                         en: "Chat restored" },
  historyRestoreErr:{ zh: "恢复失败：无效数据",                   en: "Restore failed: invalid data" },

  // confirm modal
  confirmTitle:     { zh: "确认删除",                           en: "Confirm Delete" },
  confirmDeleteMsg: { zh: "确定要删除这条对话记录吗？此操作不可撤销。", en: "Delete this chat record? This cannot be undone." },
  confirmBtn:       { zh: "确定",                               en: "Confirm" },
  cancelBtn:        { zh: "取消",                               en: "Cancel" },
};

// ---- source labels ----
const SOURCE = {
  document:         { zh: "全文",                             en: "Full document" },
  selectionWord:    { zh: "选中文字",                         en: "Selection" },
  sheet:            { zh: "当前工作表",                       en: "Current sheet" },
  selectionSheet:   { zh: "当前选区",                         en: "Current selection" },
  allSlides:        { zh: "所有幻灯片",                       en: "All slides" },
};

// ---- system prompt (kept in Chinese per user request) ----
const SYS_PROMPTS: Record<string, string> = {
  word: `你是嵌入在 Word 中的写作助手 ${brandAgentName()}。可以读取文档内容、直接修改文档（插入段落/标题/表格、替换选区、设置格式）、或对选区添加批注。`,
  excel: `你是嵌入在 Excel 中的数据助手 ${brandAgentName()}。可以读取选区或工作表数据、解读/核对、写入单元格或公式、生成图表与透视表、或把分析结论写入专门的工作表。`,
  powerpoint: `你是嵌入在 PowerPoint 中的演示助手 ${brandAgentName()}。可以读取选中文字或全部幻灯片、改写文案、或新增幻灯片（标题+要点）。`,
  unknown: `你是嵌入在 Office 中的助手 ${BRAND_NAME}。`,
  toolsIntro: "你有若干可用工具。请自主判断：\n" +
    "- 纯问答（解释、建议、翻译、写公式说明等）→ 直接回答，不要调用工具；\n" +
    "- 需要读取当前文档/选区才能回答 → 先调用对应的读取工具，拿到内容后再回答；\n" +
    "- 用户要求修改文档/写入数据/生成图表等 → 调用对应的编辑工具执行；\n" +
    "- 需要联网获取最新信息（web_search 可用时）→ 先问确认，用户同意后再调。\n" +
    "\n" +
    "Excel 数据读取规则：\n" +
    "- excel_get_sheet/excel_get_selection 每次最多返回 200 行。返回结果中 totalRows 大于本次返回行数时，说明数据被截断，你必须用 excel_get_sheet 的 fromRow 参数分批补读（如 fromRow=200/400…），直到读完所有行。\n" +
    "- 需要跨表分析时，先调用 excel_list_sheets 了解有哪些表，再逐一读取。\n" +
    "\n" +
    "重要规则：\n" +
    "- 当你主动提议做某事（如「需要我写入文档吗？」），用户回复「需要」「是」「可以」「好的」等肯定词时，你必须立即调用对应的编辑工具，不要再次用文字回复！\n" +
    "- 只用显式提供的工具操作文档，不要承诺你做不到的事情（如「生成 .tex 文件到磁盘」「发邮件」「调用外部 API」等）。\n" +
    "- 生成大段内容（代码、LaTeX、长文等）时，直接调用 word_apply_edits 写入文档，不要整段堆在聊天回复中。\n" +
    "\n" +
    "写入文档的内容格式规则（必须遵守）：\n" +
    "- 传给编辑工具的 text/title/value 一律是「单行纯文本」：字符串内部禁止包含换行，更禁止包含字面的反斜杠转义（如 \\n、\\t）——它们会被原样写进文档，用户会看到「\\n」这样的乱码。\n" +
    "- 多行/多段内容必须拆分：Word 用多个 insertParagraph 操作（每段一个），PPT 用 bullets 数组（每条一个），Excel 用二维 values（每格一个）。\n" +
    "- 写入 Word 的内容不要使用 Markdown 语法（如 **、##、-、``` 等）：标题用 style 参数表达，列表拆成多个段落，加粗/斜体用 bold/italic 参数表达。\n" +
    "- 聊天回复（不写入文档时）可以正常使用 Markdown 排版。\n" +
    "- 当执行了工具，请用一句话说明做了什么。",
};

// ---- i18n entry point ----
let currentLang: Lang = "zh";

export function setLang(lang: Lang) {
  currentLang = lang;
  document.documentElement.lang = lang;
}

export function getLang(): Lang {
  return currentLang;
}

export function t(strs: { zh: string; en: string }): string {
  return strs[currentLang];
}
export function tf(fn: (...args: any[]) => { zh: string; en: string }, ...args: any[]): string {
  return fn(...args)[currentLang];
}

/** Walk the DOM and replace text for all [data-i18n] and [data-i18n-placeholder] elements. */
export function applyI18n(root: HTMLElement | Document = document) {
  // [data-i18n] — text content
  root.querySelectorAll("[data-i18n]").forEach((el) => {
    const raw = el.getAttribute("data-i18n")!;
    const key = raw.startsWith("settings.") ? raw.slice(9) : raw.startsWith("topbar.") ? raw.slice(7) : raw;
    const strs = SETTINGS[key];
    if (!strs) return;
    if (el.querySelector("code")) {
      el.innerHTML = strs[currentLang];
    } else {
      el.textContent = strs[currentLang];
    }
  });
  // [data-i18n-placeholder] — placeholder attributes (textarea/input)
  root.querySelectorAll("[data-i18n-placeholder]").forEach((el) => {
    const raw = el.getAttribute("data-i18n-placeholder")!;
    const key = raw.startsWith("topbar.") ? raw.slice(7) : raw;
    const strs = SETTINGS[key];
    if (strs) {
      (el as HTMLInputElement | HTMLTextAreaElement).placeholder = strs[currentLang];
    }
  });
  // [data-i18n-title] — title attributes (for icon buttons etc.)
  root.querySelectorAll("[data-i18n-title]").forEach((el) => {
    const raw = el.getAttribute("data-i18n-title")!;
    const key = raw.startsWith("topbar.") ? raw.slice(7) : raw;
    const strs = SETTINGS[key];
    if (strs) {
      el.setAttribute("title", strs[currentLang]);
      el.setAttribute("aria-label", strs[currentLang]);
    }
  });
}

// ---- exported bundles ----
export const L = {
  BOOT,
  STATUS,
  ACTIONS,
  EXPORT,
  FOOTER,
  WELCOME,
  WELCOME_UNSUPPORTED,
  SETTINGS,
  SOURCE,
  SYS_PROMPTS,
};
